import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/common/Layout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { ToastProvider } from '@/components/ui/Toast';
import { useAuthStore } from '@/store/auth.store';
import { useWorkspaceStore } from '@/store/workspace.store';
import { vaAMiAlta, vaAMiConversia } from '@/lib/mi-conversia';
import MiConversia from '@/pages/mi-conversia/MiConversia';
import EntrarGlobal from '@/pages/entrar-global/EntrarGlobal';
import MiAlta from '@/pages/alta/MiAlta';
import Login from '@/pages/login/Login';
import AcceptInvite from '@/pages/auth/AcceptInvite';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import TecnicoJobs from '@/pages/tecnico/TecnicoJobs';
import Dashboard from '@/pages/dashboard/Dashboard';
import Agents from '@/pages/agents/Agents';
import Channels from '@/pages/channels/Channels';
import Conversations from '@/pages/conversations/Conversations';
import ConversationDetail from '@/pages/conversations/ConversationDetail';
import Crm from '@/pages/crm/Crm';
import Contacts from '@/pages/contacts/Contacts';
import Campaigns from '@/pages/campaigns/Campaigns';
import Settings from '@/pages/settings/Settings';
import { InstallationConfigPage } from '@/pages/installation/InstallationConfigPage';
import Users from '@/pages/users/Users';
import Roles from '@/pages/roles/Roles';
import Aprobaciones from '@/pages/aprobaciones/Aprobaciones';
import Integraciones from '@/pages/integraciones/Integraciones';
import Employees from '@/pages/employees/Employees';
import Jobs from '@/pages/jobs/Jobs';
import Despachos from '@/pages/despachos/Despachos';
import Tracking from '@/pages/tracking/Tracking';
import Clientes from '@/pages/clientes/Clientes';
import ClienteDetail from '@/pages/clientes/ClienteDetail';
import Sistema from '@/pages/sistema/Sistema';
import Entrenamiento from '@/pages/entrenamiento/Entrenamiento';
import Consumos from '@/pages/consumos/Consumos';
import Gastos from '@/pages/gastos/Gastos';
import Limites from '@/pages/limites/Limites';
import Vigia from '@/pages/vigia/Vigia';
import Inspector from '@/pages/inspector/Inspector';
import InspectorAjustes from '@/pages/inspector/InspectorAjustes';
import NuevoNegocio from '@/pages/negocios/NuevoNegocio';
import ClientesDeConversia from '@/pages/negocios/Clientes';
import Solicitudes from '@/pages/negocios/Solicitudes';
import Agenda from '@/pages/agenda/Agenda';
import Hoy from '@/pages/hoy/Hoy';
import MeToca from '@/pages/metoca/MeToca';
import Personas from '@/pages/personas/Personas';
import FichaDePersona from '@/pages/personas/FichaDePersona';
import AthenaPagina from '@/pages/athena/Athena';
import ChatDeAthena from '@/pages/chat/ChatDeAthena';
import Chats from '@/pages/chats/Chats';
import FrentesPagina from '@/pages/frentes/Frentes';
import Importar from '@/pages/importar/Importar';
import Salud from '@/pages/salud/Salud';
import ClaveDeIA from '@/pages/ajustes/ClaveDeIA';
import Cola from '@/pages/cola/Cola';
import Metricas from '@/pages/cola/Metricas';

/**
 * Shell admin con guardia de rol: un TECNICO no ve el CRM. Se le rebota a su
 * vista acotada (/mis-trabajos). El resto entra al <Layout> completo.
 *
 * Un negocio que no es `nuevo` (reflejo, VIP, pendiente) vive en otro sistema:
 * su panel normal saldría vacío, así que va a «Mi Conversia». El Layout carga
 * la lista de negocios; en cuanto llega, esto se vuelve a evaluar. El negocio
 * propio del OWNER es `nuevo` y entra a su panel de siempre.
 */
function AdminShell() {
  const role = useAuthStore((s) => s.user?.role);
  const activo = useWorkspaceStore((s) => s.items.find((w) => w.id === s.activeId));
  const cargado = useWorkspaceStore((s) => s.cargado);
  const negocios = useWorkspaceStore((s) => s.items.length);
  if (role === 'TECNICO') return <Navigate to="/mis-trabajos" replace />;
  if (vaAMiConversia(activo)) return <Navigate to="/mi-conversia" replace />;
  // Cuenta sin ningún negocio (cliente nuevo que se registró): a su alta.
  if (vaAMiAlta({ role, cargado, negocios })) return <Navigate to="/mi-alta" replace />;
  return <Layout />;
}

/** Router raíz de Conversia. Rutas privadas bajo <Layout> protegido. */
export default function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Ciclo de cuentas (páginas públicas): canje de invitación y reset. */}
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        {/* Entrar desde el Conversia global con un pase de un minuto (pages/entrar-global). */}
        <Route path="/entrar-global" element={<EntrarGlobal />} />
        {/* El registro público de clientes se quitó: el global es interno (Kervens, 18-oct-2026). */}

        {/* La solicitud de alta de una cuenta sin negocio: sin el menú. */}
        <Route
          path="/mi-alta"
          element={
            <ProtectedRoute>
              <MiAlta />
            </ProtectedRoute>
          }
        />

        {/* Vista del técnico: standalone (sin sidebar/CRM), solo requiere sesión. */}
        <Route
          path="/mis-trabajos"
          element={
            <ProtectedRoute>
              <TecnicoJobs />
            </ProtectedRoute>
          }
        />

        {/* El programa real de un cliente reflejo/VIP: a pantalla completa, sin
            el menú de Conversia. */}
        <Route
          path="/mi-conversia"
          element={
            <ProtectedRoute>
              <MiConversia />
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <AdminShell />
            </ProtectedRoute>
          }
        >
          {/* La raiz es HOY, no el Dashboard de ventas: el mandato Athena lo
              saca de este producto. El Dashboard sigue existiendo para los
              negocios que si lo usan, en /dashboard. */}
          <Route path="/" element={<Hoy />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/me-toca" element={<MeToca />} />
          <Route path="/personas" element={<Personas />} />
          <Route path="/personas/:id" element={<FichaDePersona />} />
          <Route path="/athena" element={<AthenaPagina />} />
          <Route path="/chats" element={<Chats />} />
          <Route path="/chats/:id" element={<ChatDeAthena />} />
          <Route path="/frentes" element={<FrentesPagina />} />
          <Route path="/importar" element={<Importar />} />
          <Route path="/salud" element={<Salud />} />
          {/* OWNER y no ADMIN: la API de claves solo acepta al dueño. Un admin
              que entra acá veria el formulario y recibiria un 403 al guardar. */}
          <Route
            path="/ajustes/clave-ia"
            element={
              <ProtectedRoute requireRole="OWNER">
                <ClaveDeIA />
              </ProtectedRoute>
            }
          />
          <Route path="/conversations" element={<Conversations />} />
          <Route path="/conversations/:id" element={<ConversationDetail />} />
          <Route path="/crm" element={<Crm />} />
          <Route path="/agents" element={<Agents />} />
          <Route path="/channels" element={<Channels />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/negocios"
            element={
              <ProtectedRoute requireRole="OWNER">
                <ClientesDeConversia />
              </ProtectedRoute>
            }
          />
          <Route
            path="/negocios/nuevo"
            element={
              <ProtectedRoute requireRole="OWNER">
                <NuevoNegocio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/negocios/solicitudes"
            element={
              <ProtectedRoute requireRole="OWNER">
                <Solicitudes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agenda"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Agenda />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspector"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Inspector />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inspector/ajustes"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <InspectorAjustes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/installation"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <InstallationConfigPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Employees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Jobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/despachos"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Despachos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Clientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clientes/:id"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <ClienteDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tracking"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Tracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              // OWNER, igual que su API: las rutas de usuarios no distinguen
              // negocio, y en manos de un ADMIN permitían resetear la clave del
              // dueño. Ver users.routes.ts.
              <ProtectedRoute requireRole="OWNER">
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles"
            element={
              // ADMIN, o quien tenga «Usuarios y roles» en su rol de departamento
              // (el ProtectedRoute lo deja pasar si el servidor puso /roles en su menú).
              <ProtectedRoute requireRole="ADMIN">
                <Roles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aprobaciones"
            element={
              // ADMIN, o quien apruebe en alguna área por su rol de departamento.
              <ProtectedRoute requireRole="ADMIN">
                <Aprobaciones />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integraciones"
            element={
              // ADMIN, o quien tenga «Integraciones» en su rol de departamento.
              <ProtectedRoute requireRole="ADMIN">
                <Integraciones />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sistema"
            element={
              // OWNER, igual que su API (requireOwner en system.routes.ts).
              <ProtectedRoute requireRole="OWNER">
                <Sistema />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metricas"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Metricas />
              </ProtectedRoute>
            }
          />
          {/* Relevo de cola (F5.6, E-012). La API responde 404 si el negocio
              no tiene el módulo; el menú tampoco lo ofrece. */}
          <Route
            path="/cola"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Cola />
              </ProtectedRoute>
            }
          />
          <Route
            path="/entrenamiento"
            element={
              <ProtectedRoute requireRole="ADMIN">
                <Entrenamiento />
              </ProtectedRoute>
            }
          />
          {/* Reservado del OWNER (F2): consumo USD y límites de gasto. La API
              ya responde 403 a un ADMIN; esto solo evita mostrarle una página
              rota. */}
          <Route
            path="/gastos"
            element={
              <ProtectedRoute requireRole="OWNER">
                <Gastos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consumos"
            element={
              <ProtectedRoute requireRole="OWNER">
                <Consumos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/limites"
            element={
              <ProtectedRoute requireRole="OWNER">
                <Limites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vigia"
            element={
              <ProtectedRoute requireRole="OWNER">
                <Vigia />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastProvider />
    </>
  );
}
